import { useToastProvider } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { EditorStatus } from '../../DocumentOverview/Context';
import {
  APICancelledError,
  TrainingAsessmentAPI,
} from '../api/trainingAssessmentApi';
import {
  TrainingAssessment,
  TrainingAssessmentPayload,
} from '../types/trainingAssessment';

export const useSaveTrainingAssessment = (
  documentId: TrainingAssessment['document_id'],
  setTrainingAssessmentStatus: (status: EditorStatus) => void,
) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: TrainingAssessment | TrainingAssessmentPayload,
    ) => {
      if (TrainingAssessment.safeParse(payload).success) {
        return TrainingAsessmentAPI.patchTrainingAssessmentForDocument(
          documentId,
          payload as TrainingAssessment,
        );
      } else {
        return TrainingAsessmentAPI.postTrainingAssessmentForDocument(
          documentId,
          payload as TrainingAssessmentPayload,
        );
      }
    },
    onMutate: async () => {
      setTrainingAssessmentStatus(EditorStatus.SAVING);
      await queryClient.cancelQueries(['trainingAssessment', documentId]);
    },
    onError: (error: any) => {
      if (error instanceof APICancelledError) {
        return;
      }

      let errorMessage = t('notifications.failedToSaveTrainingAssessment');

      if (error.response?.data?.slug === 'multiple-correct-answers') {
        errorMessage += t('notifications.ensureOnlyOneAnswerCorrect');

        setTrainingAssessmentStatus(EditorStatus.STABLE);
      } else {
        setTrainingAssessmentStatus(EditorStatus.ERROR);
      }

      showToast({
        'data-cy': 'use-save-training-assessment-notify-error-toast',
        status: 'error',
        title: t('notifications.error'),
        description: errorMessage,
      });
    },
    onSuccess: async (trainingAssessment) => {
      setTrainingAssessmentStatus(EditorStatus.SAVED);
      await queryClient.cancelQueries(['trainingAssessment', documentId]);
      queryClient.setQueryData(
        ['trainingAssessment', documentId],
        trainingAssessment,
      );
    },
  });
};
